import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, EyeOff, GripVertical } from 'lucide-react';
import { adminGetSocialMedia, adminCreateSocialMedia, adminUpdateSocialMedia, adminDeleteSocialMedia } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ICON_OPTIONS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'tiktok', label: 'TikTok' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

const COLOR_PRESETS = [
  { value: '#1877F2', label: 'Facebook Blue' },
  { value: '#E4405F', label: 'Instagram Pink' },
  { value: '#000000', label: 'TikTok Black' },
  { value: '#1DA1F2', label: 'Twitter Blue' },
  { value: '#FF0000', label: 'YouTube Red' },
  { value: '#0A66C2', label: 'LinkedIn Blue' },
  { value: '#25D366', label: 'WhatsApp Green' },
];

const emptySocial = {
  name: '',
  url: '',
  icon: 'facebook',
  color: '#1877F2',
  is_active: true,
  sort_order: 0,
};

export default function AdminSocialMedia() {
  const [socials, setSocials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedSocial, setSelectedSocial] = useState(null);
  const [formData, setFormData] = useState(emptySocial);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSocials = async () => {
    try {
      const res = await adminGetSocialMedia();
      setSocials(res.data);
    } catch (err) {
      console.error('Error fetching social media:', err);
      toast.error('Gabim gjatë marrjes së rrjeteve sociale');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSocials();
  }, []);

  const handleOpenCreate = () => {
    setSelectedSocial(null);
    setFormData({
      ...emptySocial,
      sort_order: socials.length + 1,
    });
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (social) => {
    setSelectedSocial(social);
    setFormData(social);
    setIsDialogOpen(true);
  };

  const handleOpenDelete = (social) => {
    setSelectedSocial(social);
    setIsDeleteDialogOpen(true);
  };

  const handleIconChange = (icon) => {
    const preset = COLOR_PRESETS.find(c => c.label.toLowerCase().includes(icon));
    setFormData({ 
      ...formData, 
      icon,
      color: preset?.value || formData.color 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (selectedSocial) {
        await adminUpdateSocialMedia(selectedSocial.id, formData);
        toast.success('Rrjeti social u përditësua!');
      } else {
        await adminCreateSocialMedia(formData);
        toast.success('Rrjeti social u shtua!');
      }
      setIsDialogOpen(false);
      fetchSocials();
    } catch (err) {
      console.error('Error saving social:', err);
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedSocial) return;

    try {
      await adminDeleteSocialMedia(selectedSocial.id);
      toast.success('Rrjeti social u fshi!');
      setIsDeleteDialogOpen(false);
      fetchSocials();
    } catch (err) {
      console.error('Error deleting social:', err);
      toast.error('Gabim gjatë fshirjes');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-social-media-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Rrjetet Sociale</h1>
          <p className="text-[#71717A]">Menaxho linket e rrjeteve sociale</p>
        </div>
        <Button 
          onClick={handleOpenCreate}
          className="bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
          data-testid="add-social-button"
        >
          <Plus size={18} className="mr-2" />
          Shto Rrjet Social
        </Button>
      </div>

      {/* Social Media List */}
      <div className="bg-white rounded-sm border border-[#E4E4E7]">
        {socials.length > 0 ? (
          <div className="divide-y divide-[#E4E4E7]">
            {socials.map((social) => (
              <div 
                key={social.id} 
                className="p-6 flex items-center justify-between gap-4 hover:bg-[#F4F4F5] transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-[#71717A]">
                    <GripVertical size={16} />
                    <span className="text-sm">{social.sort_order}</span>
                  </div>
                  
                  <div 
                    className="w-10 h-10 rounded flex items-center justify-center text-white"
                    style={{ backgroundColor: social.color }}
                  >
                    <span className="text-xs font-bold uppercase">
                      {social.icon?.charAt(0)}
                    </span>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-[#0A0A0A]">{social.name}</h3>
                    <p className="text-sm text-[#71717A] truncate max-w-xs">{social.url}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${
                    social.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {social.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                    {social.is_active ? 'Aktiv' : 'Joaktiv'}
                  </span>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(social)}
                      className="text-[#71717A] hover:text-[#D4B982]"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenDelete(social)}
                      className="text-[#71717A] hover:text-red-500"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <p className="text-[#71717A]">Nuk ka rrjete sociale ende</p>
          </div>
        )}
      </div>

      {/* Preview */}
      <div className="mt-8 p-6 bg-[#F4F4F5] rounded-sm">
        <h3 className="font-semibold text-[#0A0A0A] mb-4">Pamja në Faqe</h3>
        <div className="flex gap-1">
          {socials.filter(s => s.is_active).map((social) => (
            <div
              key={social.id}
              className="w-10 h-10 flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: social.color }}
            >
              {social.icon?.charAt(0).toUpperCase()}
            </div>
          ))}
        </div>
        <p className="text-xs text-[#71717A] mt-2">
          Këto ikona do të shfaqen në anën e djathtë të faqes. Kur përdoruesi e mban miun mbi to, shfaqet emri.
        </p>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedSocial ? 'Ndrysho Rrjetin Social' : 'Shto Rrjet Social'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div>
              <Label htmlFor="name">Emri *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
                placeholder="Facebook"
                data-testid="social-name"
              />
            </div>

            <div>
              <Label htmlFor="url">URL *</Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                required
                className="mt-1"
                placeholder="https://facebook.com/..."
                data-testid="social-url"
              />
            </div>

            <div>
              <Label htmlFor="icon">Ikona</Label>
              <Select value={formData.icon} onValueChange={handleIconChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="color">Ngjyra</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-10 rounded cursor-pointer border border-[#E4E4E7]"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1"
                  placeholder="#1877F2"
                />
              </div>
              <div className="flex gap-2 mt-2">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: preset.value })}
                    className="w-6 h-6 rounded border-2 border-white shadow"
                    style={{ backgroundColor: preset.value }}
                    title={preset.label}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="sort_order">Renditja</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                className="mt-1 w-24"
                min={0}
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
              <Label htmlFor="is_active">Aktiv</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Anulo
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A]"
              >
                {isSubmitting ? 'Duke ruajtur...' : (selectedSocial ? 'Ruaj' : 'Shto')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmo Fshirjen</AlertDialogTitle>
            <AlertDialogDescription>
              Jeni të sigurt që dëshironi të fshini "{selectedSocial?.name}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulo</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Fshi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
